import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { Boundary } from '~/components'
import { BotIcon } from '~/components/BotIcon'
import { Link, Avatar } from '~/elements'
import { useIsAuthorized } from '~/hooks'
import { getQueryOptions, useSuspenseQuery } from '../../../services'
import { cn } from '../../../utils'
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Separator,
} from '@bpinternal/ui-kit-next'
import { MessagesAnalyticsWidget } from './MessagesAnalyticsWidget'
import { ErrorsAnalyticsWidget } from './ErrorsAnalyticsWidget'
import { BotCardDropdown } from './BotCardDropdown'
import { IntegrationIcon } from '~/features/integrations/components'
import { Edit, EllipsisVertical } from 'lucide-react'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { type Bot } from '../hooks/useBotSort'
import { getCdmStudioUrl } from '~/shared'
import { AlwaysAliveBadge } from './AlwaysAliveBadge'
import { botQueryOptions } from '~/queries/bots'
import { useIntegrations } from '~/hooks/integrations/useIntegrations'

type BotPreviewProps = {
  bot: Bot
  workspaceId: string
  userId: string
  showChart?: boolean
  className?: string
}

export const BotCardView = ({ bot, workspaceId, userId, className, showChart = true }: BotPreviewProps) => {
  const workspace = useTanstackSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId })).data
  const authorize = useIsAuthorized({ userId, workspaceId: workspace.id })
  const navigate = useNavigate()
  const { data: bpBot } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', {
    botId: bot.id,
    workspaceId: workspace.id,
  })

  const [alwaysAlivePending, setAlwaysAlivePending] = useState(false)

  const isAuthorizedToUpdateBot = authorize('bots.update')

  const botDetailed = useTanstackSuspenseQuery(botQueryOptions({ botId: bot.id, workspaceId })).data

  const botIntegrations = useIntegrations({
    workspaceId,
    integrationIds: Object.values(botDetailed.integrations).map((i) => i.id),
  })

  const communicationChannelIntegrations = botIntegrations.filter((integration) => {
    return integration?.interfaces['typing-indicator'] !== undefined
  })

  const useConfigStore = useMemo(() => getUseConfigStore({ botId: bot.id, workspaceId }), [bot.id, workspaceId])
  const botAvatar = useConfigStore((state) => state.botAvatar)

  const openInStudio = () => {
    window.open(getCdmStudioUrl(bot.id))
  }

  return (
    <Card
      className={cn(
        'group relative flex min-h-[200px] min-w-[260px] max-w-full cursor-pointer flex-col justify-between p-4 @container',
        className
      )}
      onClick={() => {
        navigate({ to: '/workspaces/$workspaceId/bots/$botId', params: { workspaceId: workspace.id, botId: bot.id } })
      }}
    >
      <CardHeader className="!p-0">
        <div className="flex items-center justify-between">
          <div className="flex w-full items-center gap-3">
            {botAvatar ? (
              <Avatar
                name={bot?.name}
                pictureUrl={botAvatar}
                className="h-10 w-10 flex-none rounded-full"
                variant="solid"
              />
            ) : (
              <BotIcon size="3" id={bot.id} className="flex-none" />
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex min-w-0 items-center justify-between">
                <Link
                  className="text-neutral block min-w-0 flex-1 overflow-hidden truncate text-base font-medium"
                  to="/workspaces/$workspaceId/bots/$botId"
                  params={{ workspaceId: workspace.id, botId: bot.id }}
                  onClick={(e) => e.stopPropagation()}
                  preload={false}
                >
                  {bot?.name}
                </Link>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <AlwaysAliveBadge alwaysAlive={bpBot?.alwaysAlive} pending={alwaysAlivePending} />
                  <Tooltip>
                    <TooltipContent>{'Edit in studio'}</TooltipContent>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation()
                          openInStudio()
                        }}
                      >
                        <Edit />
                      </Button>
                    </TooltipTrigger>
                  </Tooltip>

                  {isAuthorizedToUpdateBot && (
                    <BotCardDropdown
                      userId={userId}
                      botId={bot.id}
                      botName={bot?.name || 'Bot'}
                      workspaceId={workspace.id}
                      setAlwaysAlivePending={setAlwaysAlivePending}
                    >
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <EllipsisVertical className="h-4 w-4" />
                      </Button>
                    </BotCardDropdown>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {bot?.deployedAt ? (
                    <>
                      Deployed {DateTime.fromISO(bot?.deployedAt).toRelative()}{' '}
                      {bot?.deployedAt && communicationChannelIntegrations.length > 0 && 'on'}
                    </>
                  ) : (
                    <>Not deployed</>
                  )}
                </span>
                {bot?.deployedAt && communicationChannelIntegrations.length > 0 && (
                  <div className="flex items-center gap-1">
                    {communicationChannelIntegrations
                      .slice(0, 2)
                      .map(
                        (integration) =>
                          integration && (
                            <IntegrationIcon key={integration.id} iconUrl={integration.iconUrl} className="h-4 w-4" />
                          )
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="py-3">
          <Separator />
        </div>
      </CardHeader>

      {showChart && (
        <Boundary className="ml-auto" suspenseFallback={null} onError={() => null}>
          <CardContent className="px-0 pb-4 pt-2">
            <div className="flex min-w-0 flex-col gap-6 pb-4 sm:flex-row sm:gap-6">
              <div className="min-w-0 flex-1">
                <MessagesAnalyticsWidget color={4} botId={bot.id} workspaceId={workspace.id} />
              </div>
              <div className="min-w-0 flex-1">
                <ErrorsAnalyticsWidget color={2} botId={bot.id} workspaceId={workspace.id} />
              </div>
            </div>
          </CardContent>
        </Boundary>
      )}
    </Card>
  )
}
